import { FH_FLAG, FH_YES_NO } from './company-form.constants';

// Invoicing classification categories computed by the backend from the Invoicing Instructions
// fields (INI-13125). The front uses them for the detail tag and the category-change modal.
export const INVOICING_CLASSIFICATION = {
  BASIC_30:         'Basic 30',
  BASIC_45:         'Basic 45',
  BASIC_PLUS_OC_GR: 'Basic + OC/GR',
  COMPLEX:          'Complex',
} as const;
export type InvoicingClassificationValue = typeof INVOICING_CLASSIFICATION[keyof typeof INVOICING_CLASSIFICATION];

// TBD-B1: tentative field name on CompanyRecord / the detail API; swap ONLY this constant once
// the backend confirms the real name.
export const CLASSIFICATION_API_FIELD = 'invoicing_classification';

// TBD-B7: billing_frequency machine values, parametrized until the CMS addition is confirmed.
// The classification-calculate overrides must carry exactly these strings.
export const BILLING_FREQUENCY_VALUE = {
  MONTHLY:       'Monthly',
  EVERY_45_DAYS: 'Every 45 Days',
} as const;

// The 12 control names that feed the classification.
export const CLASSIFICATION_FIELD = {
  TYPE_OF_COMPANY:                        'type_of_company',
  BILLING_FREQUENCY:                      'billing_frequency',
  REQUIRES_GOOD_RECEIPT:                  'requires_good_receipt',
  IS_OC_REQUIRED:                         'is_oc_required',
  IS_MIGO_NUMBER_NEEDED:                  'is_migo_number_needed',
  IS_HES_NUMBER_NEEDED:                   'is_hes_number_needed',
  IS_ENTRANCE_NUMBER_NEEDED:              'is_entrance_number_needed',
  IS_ANOTHER_NUMBER_NEEDED:               'is_another_number_needed',
  REQUIRES_INVOICE_UPLOAD:                'requires_invoice_upload',
  IS_CONCILIATION_LETTER_NEEDED:          'is_conciliation_letter_needed',
  HAS_SPECIAL_INVOICE_ACCEPTANCE_PROCESS: 'has_special_invoice_acceptance_process',
  IS_PROOF_OF_EXECUTION_REQUIRED:         'is_proof_of_execution_required',
} as const;

// INI-13125-3: per-item `status` of POST /companies/classification-calculate. HTTP 200 can still
// carry a non-calculated item — branch on these, never on the HTTP code alone.
export const CLASSIFICATION_CALCULATE_STATUS = {
  CALCULATED:            'calculated',
  SKIPPED_MISSING_FIELDS: 'skipped_missing_fields',
  ERROR:                 'error',
} as const;

// The 11 override `fields` keys the calculate endpoint accepts (exact backend names). The engine
// always uses the saved type_of_company, so TYPE_OF_COMPANY is deliberately absent — the backend
// ignores it in overrides.
export const CLASSIFICATION_OVERRIDE_FIELDS: readonly string[] = [
  CLASSIFICATION_FIELD.BILLING_FREQUENCY,
  CLASSIFICATION_FIELD.REQUIRES_GOOD_RECEIPT,
  CLASSIFICATION_FIELD.IS_OC_REQUIRED,
  CLASSIFICATION_FIELD.IS_MIGO_NUMBER_NEEDED,
  CLASSIFICATION_FIELD.IS_HES_NUMBER_NEEDED,
  CLASSIFICATION_FIELD.IS_ENTRANCE_NUMBER_NEEDED,
  CLASSIFICATION_FIELD.IS_ANOTHER_NUMBER_NEEDED,
  CLASSIFICATION_FIELD.REQUIRES_INVOICE_UPLOAD,
  CLASSIFICATION_FIELD.IS_CONCILIATION_LETTER_NEEDED,
  CLASSIFICATION_FIELD.HAS_SPECIAL_INVOICE_ACCEPTANCE_PROCESS,
  CLASSIFICATION_FIELD.IS_PROOF_OF_EXECUTION_REQUIRED,
];

// Builds the calculate endpoint's override `fields` from a PUT diff body: only the 11 keys the
// endpoint accepts, with booleans normalized to real booleans (the diff can carry 'true'/'false'
// strings or legacy 'yes'/'no' selects). Keys with null/undefined values are omitted — the backend
// ignores nulls, and an omitted key means "use the saved value".
export function toClassificationOverrides(
  diffBody: Record<string, unknown> | null | undefined,
): Record<string, string | boolean> {
  const overrides: Record<string, string | boolean> = {};
  if (!diffBody) {
    return overrides;
  }
  CLASSIFICATION_OVERRIDE_FIELDS.forEach(key => {
    if (!(key in diffBody) || diffBody[key] === null || diffBody[key] === undefined) {
      return;
    }
    const value = diffBody[key];
    overrides[key] = key === CLASSIFICATION_FIELD.BILLING_FREQUENCY
      ? String(value)
      : value === true || value === FH_FLAG.TRUE || value === FH_YES_NO.YES;
  });
  return overrides;
}

// Copy for the classification-change warning dialog (INI-13125), per the approved mock. BODY is
// the transition variant (current → next); BODY_NEW_CLASSIFICATION is the first-classification
// variant shown to Draft companies on submit, which have no current category yet.
export const CLASSIFICATION_CHANGE_DIALOG_MESSAGE = {
  TITLE:                  'Confirm changes',
  BODY:                   'Your company’s classification is changing. This update will apply to your next billing cycle; any invoices currently in progress will not be affected:',
  BODY_NEW_CLASSIFICATION: 'Your company will be classified as:',
  KEEP_EDITING:           'Keep editing',
  CONFIRM:                'Confirm changes',
} as const;

// Copy for the classification calculation-failure dialog (INI-13125): the dry-run could not run.
// The continue button's label is per-flow (Save anyway / Submit anyway) and travels in the data.
export const CLASSIFICATION_CALCULATION_ERROR_DIALOG_MESSAGE = {
  TITLE: 'Unable to calculate classification',
  BODY:  'We could not calculate the invoicing classification. Would you like to try again?',
  RETRY: 'Try again',
} as const;

// Close values of the calculation-failure dialog: Try again re-runs the dry-run, the labeled
// continue proceeds without the preview, and a dismissal (X / ESC) closes undefined/null.
export const CLASSIFICATION_CALCULATION_ERROR_DIALOG_RESULT = {
  RETRY:    'retry',
  CONTINUE: 'continue',
} as const;

// Per-flow label for the calculation-failure dialog's continue button.
export const CLASSIFICATION_CONTINUE_LABEL = {
  SAVE:   'Save anyway',
  SUBMIT: 'Submit anyway',
} as const;

// Internal outcome of the classification guard: proceed with the save/submit, abort the action,
// or re-run the dry-run (failure dialog's Try again).
export const CLASSIFICATION_GUARD_DECISION = {
  SAVE:  'save',
  ABORT: 'abort',
  RETRY: 'retry',
} as const;
export type ClassificationGuardDecision =
  typeof CLASSIFICATION_GUARD_DECISION[keyof typeof CLASSIFICATION_GUARD_DECISION];

// Outcome of the detail page's submit-for-verification guard: run the lifecycle call directly
// (the transition modal confirmed — it replaces the generic confirm), fall back to the generic
// confirm dialog (unchanged category, or the failure dialog's Continue anyway), re-run the
// dry-run, or do nothing.
export const CLASSIFICATION_SUBMIT_STEP = {
  RUN:     'run',
  CONFIRM: 'confirm',
  RETRY:   'retry',
  ABORT:   'abort',
} as const;
export type ClassificationSubmitStep = typeof CLASSIFICATION_SUBMIT_STEP[keyof typeof CLASSIFICATION_SUBMIT_STEP];

// cd-status modifier per category for the classification chip (detail + edit views). An unknown
// value falls back to the Complex modifier, matching the category's own fallback semantics.
export function classificationCssClass(classification: InvoicingClassificationValue): string {
  switch (classification) {
    case INVOICING_CLASSIFICATION.BASIC_30: return 'basic-30';
    case INVOICING_CLASSIFICATION.BASIC_45: return 'basic-45';
    case INVOICING_CLASSIFICATION.BASIC_PLUS_OC_GR: return 'basic-plus-oc-gr';
    default: return 'complex';
  }
}
